import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from 'react-bootstrap';
import PageHeader from '../../components/ui/PageHeader';
import { listarRango } from '../../services/turneroService';
import { colorTramite, bgTramite, formatearTipoTramite } from '../../constants/tiposTramiteTurno';
import { AREAS_ATENCION, etiquetaArea } from '../../constants/areasAtencion';
import { estiloEstado, ESTILO_SOBRETURNO } from '../../constants/estadosTurno';
import {
    MOTIVOS_NO_AVANCE,
    etiquetaMotivoNoAvance,
    etiquetaResultadoAtencion,
} from '../../constants/resultadosAtencion';
import DatosMaestrosService from '../../services/datosMaestrosService';
import { ModalDetalleTurno } from './TurneroPage';
import BadgeReingreso from '../../components/recepcion/BadgeReingreso';
import RielTurno from '../../components/recepcion/RielTurno';
import ChipMotivoExpres from '../../components/recepcion/ChipMotivoExpres';
import ModalAgregarVolverAContactar from '../../components/recepcion/ModalAgregarVolverAContactar';
import ModalReingresoCliente from '../../components/recepcion/ModalReingresoCliente';
import { turnosEnListado } from '../../services/volverAContactarService';
import { toast } from 'react-toastify';
import { ESTILO_EXPRES } from '../../constants/tramiteExpres';
import styles from '../../style/pages/Turnero.module.css';
import '../../style/pages/HomePage.css';
import { apellidoNombre } from '../../utils/nombrePersona';
import useBreakpoint from '../../hooks/useBreakpoint';
import BottomSheet from '../../components/mobile/BottomSheet';
import BotonFiltros from '../../components/mobile/BotonFiltros';
import ChipsFiltros from '../../components/mobile/ChipsFiltros';
import { coincideBusqueda } from '../../utils/textoComparable';

const MESES = [
    { valor: 1, etiqueta: 'Enero' },
    { valor: 2, etiqueta: 'Febrero' },
    { valor: 3, etiqueta: 'Marzo' },
    { valor: 4, etiqueta: 'Abril' },
    { valor: 5, etiqueta: 'Mayo' },
    { valor: 6, etiqueta: 'Junio' },
    { valor: 7, etiqueta: 'Julio' },
    { valor: 8, etiqueta: 'Agosto' },
    { valor: 9, etiqueta: 'Septiembre' },
    { valor: 10, etiqueta: 'Octubre' },
    { valor: 11, etiqueta: 'Noviembre' },
    { valor: 12, etiqueta: 'Diciembre' },
];

// Hasta dónde hacia atrás se ofrece elegir año. El turnero histórico importado por CSV
// no tiene turnos anteriores a esto, así que no tiene sentido ofrecer más.
const ANIOS_HACIA_ATRAS = 6;

function dosDigitos(n) {
    return String(n).padStart(2, '0');
}

function diasDelMes(anio, mes) {
    return new Date(anio, mes, 0).getDate();
}

// Convierte un ISO date string "YYYY-MM-DD" a una Date local (sin desfase UTC).
function isoAFecha(iso) {
    const [y, m, d] = String(iso).split('-').map(Number);
    return new Date(y, m - 1, d);
}

function etiquetaFecha(iso) {
    const d = isoAFecha(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('es-AR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
}

function nombreOperador(turno) {
    return turno?.operadorNombre || turno?.usuarioAtiendeNombre || '';
}

export default function HistoricoTurnosPage() {
    const navigate = useNavigate();
    const { esMobile } = useBreakpoint();
    const [sheetFiltrosAbierto, setSheetFiltrosAbierto] = useState(false);
    const montadoRef = useRef(false);

    const hoy = useMemo(() => new Date(), []);
    // El día arranca sin selección: se muestra el mes completo, que es la vista útil por
    // defecto. Mes y año arrancan en el actual.
    const [anio, setAnio] = useState(hoy.getFullYear());
    const [mes, setMes] = useState(hoy.getMonth() + 1);
    const [dia, setDia] = useState('');

    const [filtroTexto, setFiltroTexto] = useState('');
    const [filtroReferencia, setFiltroReferencia] = useState('');
    const [filtroOperador, setFiltroOperador] = useState('');
    const [filtroArea, setFiltroArea] = useState('');
    const [filtroMotivo, setFiltroMotivo] = useState('');
    const [filtroResultado, setFiltroResultado] = useState('');
    // Catálogo editable (Datos Maestros > Resultados de la Atención, migración V224): el filtro
    // tiene que ofrecer lo que el estudio haya cargado, no una lista fija.
    const [resultadosAtencion, setResultadosAtencion] = useState([]);

    const [turnos, setTurnos] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState('');
    const [turnoSel, setTurnoSel] = useState(null);
    // Listado "Volver a contactar" (V261): qué turnos del rango ya están cargados (marca de la
    // fila y texto del botón del detalle) y el turno que se está agregando.
    const [idsVolverAContactar, setIdsVolverAContactar] = useState(() => new Set());
    const [turnoParaAgregar, setTurnoParaAgregar] = useState(null);
    // Volvió cliente / Volvió de ANSES desde el detalle (Modal de Bootstrap: lo abre la página).
    const [turnoReingreso, setTurnoReingreso] = useState(null);

    // El rango que se le pide al backend sale de día/mes/año: con día elegido es ese único
    // día, sin día es el mes entero y sin mes ("Todo el año") es el año completo.
    const { desde, hasta } = useMemo(() => {
        if (!mes) {
            return { desde: `${anio}-01-01`, hasta: `${anio}-12-31` };
        }
        if (dia) {
            const iso = `${anio}-${dosDigitos(mes)}-${dosDigitos(dia)}`;
            return { desde: iso, hasta: iso };
        }
        return {
            desde: `${anio}-${dosDigitos(mes)}-01`,
            hasta: `${anio}-${dosDigitos(mes)}-${dosDigitos(diasDelMes(anio, mes))}`,
        };
    }, [anio, mes, dia]);

    const cargarTurnos = useCallback(async () => {
        setCargando(true);
        setError('');
        try {
            // La marca de "Volver a contactar" es un extra: si falla, el histórico se ve igual.
            const [datos, idsEnListado] = await Promise.all([
                // Con todos los cancelados del rango: es un histórico, y sin esto los cancelados hace más
                // de 24 h no aparecían al elegir un período (reporte de Joaco, 20-oct-2026).
                listarRango(desde, hasta, true),
                turnosEnListado(desde, hasta).catch(() => []),
            ]);
            if (!montadoRef.current) return;
            setIdsVolverAContactar(new Set(idsEnListado.map(Number)));
            const finalizados = (Array.isArray(datos) ? datos : [])
                .filter((t) => String(t?.estado || '').trim().toUpperCase() === 'FINALIZADO');
            setTurnos(finalizados);
        } catch (e) {
            if (!montadoRef.current) return;
            setError(e?.response?.data?.mensaje || 'No se pudo cargar el histórico de turnos.');
            setTurnos([]);
        } finally {
            if (montadoRef.current) setCargando(false);
        }
    }, [desde, hasta]);

    useEffect(() => {
        montadoRef.current = true;
        return () => { montadoRef.current = false; };
    }, []);

    useEffect(() => {
        cargarTurnos();
    }, [cargarTurnos]);

    useEffect(() => {
        DatosMaestrosService.getResultadosAtencion()
            .then((res) => { if (montadoRef.current) setResultadosAtencion(res.data || []); })
            .catch(() => { if (montadoRef.current) setResultadosAtencion([]); });
    }, []);

    // Si se cambia a un mes más corto, un día ya elegido puede quedar fuera de rango.
    useEffect(() => {
        if (mes && dia && Number(dia) > diasDelMes(anio, mes)) setDia('');
    }, [anio, mes, dia]);

    const aniosDisponibles = useMemo(() => {
        const actual = hoy.getFullYear();
        return Array.from({ length: ANIOS_HACIA_ATRAS + 1 }, (_, i) => actual - i);
    }, [hoy]);

    // Las opciones de "quién atendió" y "referencia" salen de los turnos cargados: son datos
    // libres, no un catálogo cerrado, así que ofrecer valores que no están en el período
    // consultado solo daría filtros que no devuelven nada.
    const operadoresDisponibles = useMemo(() => {
        const nombres = new Set();
        turnos.forEach((t) => { const n = nombreOperador(t); if (n) nombres.add(n); });
        return Array.from(nombres).sort((a, b) => a.localeCompare(b, 'es'));
    }, [turnos]);

    const referenciasDisponibles = useMemo(() => {
        const valores = new Set();
        turnos.forEach((t) => { if (t?.referencia) valores.add(String(t.referencia)); });
        return Array.from(valores).sort((a, b) => a.localeCompare(b, 'es'));
    }, [turnos]);

    const grupos = useMemo(() => {
        const coincide = (t) => {
            // Sin tildes y por palabras: ver coincideBusqueda.
            if (!coincideBusqueda([t.apellido, t.nombre, t.cuil, t.dni], filtroTexto)) return false;
            if (filtroReferencia && String(t.referencia || '') !== filtroReferencia) return false;
            if (filtroOperador && nombreOperador(t) !== filtroOperador) return false;
            if (filtroArea && String(t.atencion || '').trim().toUpperCase() !== filtroArea) return false;
            if (filtroMotivo && String(t.motivoNoAvance || '').trim().toUpperCase() !== filtroMotivo) return false;
            // El resultado guardado es el nombre del catálogo tal cual (V224), así que se
            // compara sin normalizar a mayúsculas: alcanza con ignorar el case de los dos lados.
            if (filtroResultado && String(t.resultadoAtencion || '').trim().toLowerCase() !== filtroResultado.trim().toLowerCase()) return false;
            return true;
        };
        const mapa = new Map();
        turnos.filter(coincide).forEach((t) => {
            if (!mapa.has(t.fechaTurno)) mapa.set(t.fechaTurno, []);
            mapa.get(t.fechaTurno).push(t);
        });
        // Histórico: lo más reciente primero, y dentro del día por horario.
        return Array.from(mapa.entries())
            .sort(([a], [b]) => String(b).localeCompare(String(a)))
            .map(([iso, lista]) => [
                iso,
                lista.slice().sort((a, b) => String(a.horario || '').localeCompare(String(b.horario || ''))),
            ]);
    }, [turnos, filtroTexto, filtroReferencia, filtroOperador, filtroArea, filtroMotivo, filtroResultado]);

    const totalFiltrado = useMemo(
        () => grupos.reduce((acc, [, lista]) => acc + lista.length, 0),
        [grupos]
    );

    const hayFiltro = filtroTexto || filtroReferencia || filtroOperador || filtroArea || filtroMotivo || filtroResultado || dia;

    const limpiarFiltros = () => {
        setFiltroTexto('');
        setFiltroReferencia('');
        setFiltroOperador('');
        setFiltroArea('');
        setFiltroMotivo('');
        setFiltroResultado('');
        setDia('');
    };

    // El histórico es de solo consulta: si desde el detalle se cambia o borra un turno,
    // se recarga el período para no dejar la lista desincronizada.
    const handleActualizado = (actualizado) => {
        setTurnoSel(actualizado);
        setTurnos((prev) => prev.map((t) => (t.idTurno === actualizado.idTurno ? actualizado : t)));
    };

    const handleEliminado = () => {
        setTurnoSel(null);
        cargarTurnos();
    };

    // Chips removibles para mobile — mismo criterio que hayFiltro: año/mes no cuentan (son el
    // período base, no un filtro que se "saca"), el resto sí.
    const chipsFiltros = [
        filtroTexto && { key: 'texto', label: `"${filtroTexto}"`, onQuitar: () => setFiltroTexto('') },
        dia && { key: 'dia', label: `Día ${dia}`, onQuitar: () => setDia('') },
        filtroOperador && { key: 'operador', label: `Atendió: ${filtroOperador}`, onQuitar: () => setFiltroOperador('') },
        filtroReferencia && { key: 'referencia', label: `Ref: ${filtroReferencia}`, onQuitar: () => setFiltroReferencia('') },
        filtroArea && { key: 'area', label: etiquetaArea(filtroArea), onQuitar: () => setFiltroArea('') },
        filtroResultado && { key: 'resultado', label: etiquetaResultadoAtencion(filtroResultado), onQuitar: () => setFiltroResultado('') },
        filtroMotivo && { key: 'motivo', label: etiquetaMotivoNoAvance(filtroMotivo), onQuitar: () => setFiltroMotivo('') },
    ].filter(Boolean);

    // Contenido de cada campo sin el wrapper de grilla de escritorio (styles.filtroCampo/
    // filtroCampoAncho) — se reusa apilado dentro del BottomSheet de mobile sin duplicar el
    // markup de cada select/input.
    const campoAnio = (
        <>
            <label className={styles.filtroLabel}><i className="bi bi-calendar-event me-2"></i>Año</label>
            <select className={`form-select ${styles.filtroInput}`} value={anio} onChange={(e) => setAnio(Number(e.target.value))}>
                {aniosDisponibles.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
        </>
    );
    const campoMes = (
        <>
            <label className={styles.filtroLabel}><i className="bi bi-calendar3 me-2"></i>Mes</label>
            <select className={`form-select ${styles.filtroInput}`} value={mes} onChange={(e) => setMes(e.target.value ? Number(e.target.value) : '')}>
                <option value="">Todo el año</option>
                {MESES.map((m) => <option key={m.valor} value={m.valor}>{m.etiqueta}</option>)}
            </select>
        </>
    );
    const campoDia = (
        <>
            <label className={styles.filtroLabel}>Día <span className={styles.filtroOpcional}>(opcional)</span></label>
            <select className={`form-select ${styles.filtroInput}`} value={dia} onChange={(e) => setDia(e.target.value)} disabled={!mes}>
                <option value="">Todo el mes</option>
                {mes && Array.from({ length: diasDelMes(anio, mes) }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
        </>
    );
    const campoBuscar = (
        <>
            <label className={styles.filtroLabel}><i className="bi bi-search me-2"></i>Buscar</label>
            <input
                type="text"
                className={`form-control ${styles.filtroInput}`}
                placeholder="Nombre, apellido, DNI o CUIL…"
                value={filtroTexto}
                onChange={(e) => setFiltroTexto(e.target.value)}
            />
        </>
    );
    const campoAtendio = (
        <>
            <label className={styles.filtroLabel}><i className="bi bi-person-badge me-2"></i>Atendió</label>
            <select className={`form-select ${styles.filtroInput}`} value={filtroOperador} onChange={(e) => setFiltroOperador(e.target.value)}>
                <option value="">Todos</option>
                {operadoresDisponibles.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
        </>
    );
    const campoReferencia = (
        <>
            <label className={styles.filtroLabel}><i className="bi bi-chat-left-text me-2"></i>Referencia</label>
            <select className={`form-select ${styles.filtroInput}`} value={filtroReferencia} onChange={(e) => setFiltroReferencia(e.target.value)}>
                <option value="">Todas</option>
                {referenciasDisponibles.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
        </>
    );
    const campoArea = (
        <>
            <label className={styles.filtroLabel}><i className="bi bi-diagram-3 me-2"></i>Área de atención</label>
            <select className={`form-select ${styles.filtroInput}`} value={filtroArea} onChange={(e) => setFiltroArea(e.target.value)}>
                <option value="">Todas</option>
                {AREAS_ATENCION.map((a) => <option key={a.valor} value={a.valor}>{a.etiqueta}</option>)}
            </select>
        </>
    );
    const campoResultado = (
        <>
            <label className={styles.filtroLabel}><i className="bi bi-flag me-2"></i>Resultado</label>
            <select className={`form-select ${styles.filtroInput}`} value={filtroResultado} onChange={(e) => setFiltroResultado(e.target.value)}>
                <option value="">Todos</option>
                    {resultadosAtencion.map((r) => <option key={r.id} value={r.nombre}>{r.nombre}</option>)}
            </select>
        </>
    );
    const campoMotivo = (
        <>
            <label className={styles.filtroLabel}><i className="bi bi-exclamation-circle me-2"></i>Motivo de no avance</label>
            <select className={`form-select ${styles.filtroInput}`} value={filtroMotivo} onChange={(e) => setFiltroMotivo(e.target.value)}>
                <option value="">Todos</option>
                {MOTIVOS_NO_AVANCE.map((m) => <option key={m.valor} value={m.valor}>{m.etiqueta}</option>)}
            </select>
        </>
    );

    return (
        <div className="homepage-container area-recepcion">
            <PageHeader
                variante="plano"
                icono="clock-history"
                titulo="Histórico de Turnos"
                descripcion="Turnos finalizados — consultá por período, quién atendió y cómo terminó la atención"
            />

            <div className={`${styles.accionesBarra} mob-grid-2col`}>
                <button className="rec-btn-secondary d-flex align-items-center gap-2" onClick={() => navigate('/recepcion')}>
                    <i className="bi bi-speedometer2" />
                    Ir a Panel Diario
                </button>
                <button className="rec-btn-secondary d-flex align-items-center gap-2" onClick={() => navigate('/recepcion/turnero')}>
                    <i className="bi bi-calendar3" />
                    Ir al Turnero
                </button>
                <button className="rec-btn-secondary d-flex align-items-center gap-2" onClick={() => navigate('/recepcion/atencion')}>
                    <i className="bi bi-arrow-right-circle-fill" />
                    Ir a cola de clientes
                </button>
            </div>

            <div className="eventos-section rec-panel">
                <div className="eventos-header-row">
                    <h2 className="eventos-section-title">
                        <i className="bi bi-check-circle-fill me-2"></i>
                        Turnos finalizados
                    </h2>
                    <Badge bg="primary" className="rec-total-badge">
                        {totalFiltrado}{totalFiltrado !== turnos.length ? ` / ${turnos.length}` : ''} turno{totalFiltrado !== 1 ? 's' : ''}
                    </Badge>
                </div>

                <div className={styles.agendaContainer}>
                    {esMobile ? (
                        <div className="mb-3">
                            <div className="d-flex align-items-center gap-2">
                                <BotonFiltros cantidad={chipsFiltros.length} onClick={() => setSheetFiltrosAbierto(true)} />
                                <span style={{ fontSize: 13, color: '#64748b' }}>
                                    {mes ? MESES.find((m) => m.valor === mes)?.etiqueta : 'Todo el año'} {anio}
                                </span>
                            </div>
                            <ChipsFiltros chips={chipsFiltros} />
                            {sheetFiltrosAbierto && (
                                <BottomSheet
                                    titulo="Filtros"
                                    onCerrar={() => setSheetFiltrosAbierto(false)}
                                    accionPrimaria={hayFiltro ? { label: 'Limpiar filtros', onClick: limpiarFiltros } : undefined}
                                >
                                    <div className="mb-3">{campoAnio}</div>
                                    <div className="mb-3">{campoMes}</div>
                                    <div className="mb-3">{campoDia}</div>
                                    <div className="mb-3">{campoBuscar}</div>
                                    <div className="mb-3">{campoAtendio}</div>
                                    <div className="mb-3">{campoReferencia}</div>
                                    <div className="mb-3">{campoArea}</div>
                                    <div className="mb-3">{campoResultado}</div>
                                    <div>{campoMotivo}</div>
                                </BottomSheet>
                            )}
                        </div>
                    ) : (
                        <div className={styles.filtrosAgenda}>
                            {/* Fila 1 — período consultado (define qué se le pide al backend) */}
                            <div className={styles.filtrosFila}>
                                <div className={styles.filtroCampo}>{campoAnio}</div>
                                <div className={styles.filtroCampo}>{campoMes}</div>
                                <div className={styles.filtroCampo}>{campoDia}</div>
                            </div>

                            {/* Fila 2 — a quién / de dónde vino */}
                            <div className={styles.filtrosFila}>
                                <div className={styles.filtroCampoAncho}>{campoBuscar}</div>
                                <div className={styles.filtroCampo}>{campoAtendio}</div>
                                <div className={styles.filtroCampo}>{campoReferencia}</div>
                            </div>

                            {/* Fila 3 — cómo terminó la atención */}
                            <div className={styles.filtrosFila}>
                                <div className={styles.filtroCampo}>{campoArea}</div>
                                <div className={styles.filtroCampo}>{campoResultado}</div>
                                <div className={styles.filtroCampoAncho}>{campoMotivo}</div>
                                {hayFiltro && (
                                    <button
                                        type="button"
                                        className={`rec-btn-secondary ${styles.filtroBotonLimpiar}`}
                                        onClick={limpiarFiltros}
                                    >
                                        Limpiar
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {cargando ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" />
                            <p className="mt-2 text-muted">Cargando histórico…</p>
                        </div>
                    ) : error ? (
                        <div className="empty-state-eventos">
                            <i className="bi bi-exclamation-triangle"></i>
                            <h5>Ocurrió un problema</h5>
                            <p>{error}</p>
                        </div>
                    ) : grupos.length === 0 ? (
                        <div className={styles.agendaVacia}>
                            <i className="bi bi-clock-history" style={{ fontSize: '3rem', color: '#cbd5e1' }} />
                            <p style={{ color: '#94a3b8', marginTop: 12 }}>
                                {turnos.length === 0
                                    ? 'No hay turnos finalizados en el período elegido.'
                                    : 'Ningún turno coincide con los filtros.'}
                            </p>
                        </div>
                    ) : (
                        grupos.map(([iso, lista]) => (
                            <div key={iso} className={styles.agendaGrupo}>
                                <div className={styles.agendaFechaHeader}>
                                    <span className={styles.agendaFechaTexto}>{etiquetaFecha(iso)}</span>
                                    <span className={styles.agendaFechaConteo}>{lista.length} turno{lista.length !== 1 ? 's' : ''}</span>
                                </div>
                                <div className={styles.agendaListaTurnos}>
                                    {lista.map((t) => {
                                        const e = estiloEstado(t.estado);
                                        const operador = nombreOperador(t);
                                        // Trámite exprés (V249): tarjeta azul clarito, con lo que
                                        // entregó o retiró en lugar del motivo de consulta.
                                        const esExpres = !!t.esTramiteExpres;
                                        return (
                                            <button
                                                key={t.idTurno}
                                                className={`rec-item ${styles.filaAgenda} ${esExpres ? styles.filaAgendaExpres : ''}`}
                                                onClick={() => setTurnoSel(t)}
                                            >
                                                <RielTurno turno={t} colorEstado={e.riel} />
                                                <div className={styles.filaAgendaHora}>
                                                    <i className="bi bi-clock me-1" />
                                                    {t.horario}
                                                </div>
                                                <div className={styles.filaAgendaCliente}>
                                                    <div className={styles.filaAgendaNombre}>
                                                        {t.tipoTurno === 'VIDEOLLAMADA' && <i className="bi bi-camera-video-fill me-1" style={{ color: '#3b82f6' }} title="Videollamada" />}
                                                        {apellidoNombre(t.apellido, t.nombre)}
                                                    </div>
                                                    <div className={styles.filaAgendaSub}>
                                                        {t.numeroIdentificacion && (
                                                            <span className="d-inline-flex align-items-center">
                                                                <i className="bi bi-person-badge me-1" />#{t.numeroIdentificacion}
                                                            </span>
                                                        )}
                                                        {operador && (
                                                            <span style={{ marginLeft: 6 }}>· Atendió {operador}</span>
                                                        )}
                                                        {t.atencion && (
                                                            <span style={{ marginLeft: 6 }}>· {etiquetaArea(t.atencion)}</span>
                                                        )}
                                                        {t.referencia && (
                                                            <span style={{ marginLeft: 6 }}>· Ref: {t.referencia}</span>
                                                        )}
                                                        {esExpres
                                                            ? <span style={{ marginLeft: 6, fontWeight: 700, color: ESTILO_EXPRES.text }}>· Trámite exprés</span>
                                                            : t.esSobreturno && <span style={{ marginLeft: 6, fontWeight: 700, color: ESTILO_SOBRETURNO.text }}>· Sobreturno</span>}
                                                    </div>
                                                    {esExpres ? (
                                                        t.expresDocumentacion && (
                                                            <div className={styles.filaAgendaMotivo} title={t.expresDocumentacion}>
                                                                <i className="bi bi-folder-symlink" style={{ color: ESTILO_EXPRES.riel }} />
                                                                <span>{t.expresDocumentacion}</span>
                                                            </div>
                                                        )
                                                    ) : t.motivo && (
                                                        <div className={styles.filaAgendaMotivo} title={t.motivo}>
                                                            <i className="bi bi-chat-left-quote-fill" />
                                                            <span>{t.motivo}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                {/* En el exprés, en lugar del tipo de trámite va por qué volvió. */}
                                                {esExpres ? (
                                                    <ChipMotivoExpres turno={t} />
                                                ) : (
                                                    <span className="rec-chip-tramite" style={{ background: bgTramite(t.tipoTramite), color: colorTramite(t.tipoTramite) }}>
                                                        {formatearTipoTramite(t.tipoTramite)}
                                                    </span>
                                                )}
                                                <div className="d-flex flex-column align-items-end gap-1">
                                                    {esExpres ? (
                                                        <BadgeReingreso turno={t} />
                                                    ) : (
                                                        <span className="rec-badge-estado" style={{ background: e.bg, border: `1px solid ${e.border}`, color: e.text }}>
                                                            {e.etiqueta}
                                                        </span>
                                                    )}
                                                    {t.resultadoAtencion && (
                                                        <span className={styles.filaAgendaSub} style={{ marginTop: 0, textAlign: 'right' }}>
                                                            {etiquetaResultadoAtencion(t.resultadoAtencion)}
                                                        </span>
                                                    )}
                                                    {t.motivoNoAvance && (
                                                        <span className={styles.filaAgendaSub} style={{ marginTop: 0, textAlign: 'right' }}>
                                                            {etiquetaMotivoNoAvance(t.motivoNoAvance)}
                                                        </span>
                                                    )}
                                                    {idsVolverAContactar.has(Number(t.idTurno)) && (
                                                        <span
                                                            className="rec-badge-estado"
                                                            style={{ background: '#ecfdf5', border: '1px solid #6ee7b7', color: '#047857' }}
                                                        >
                                                            <i className="bi bi-telephone-outbound me-1" />Volver a contactar
                                                        </span>
                                                    )}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {turnoSel && (
                <div className={styles.overlay} onClick={() => setTurnoSel(null)}>
                    <div
                        className={`${styles.contenidoModal} ${styles.contenidoModalDetalle}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <ModalDetalleTurno
                            turnoSel={turnoSel}
                            onCerrar={() => setTurnoSel(null)}
                            onActualizado={handleActualizado}
                            onEliminado={handleEliminado}
                            onAsistioRegistrar={() => setTurnoSel(null)}
                            onVolvioCliente={(t) => { setTurnoSel(null); setTurnoReingreso(t); }}
                            enVolverAContactar={idsVolverAContactar.has(Number(turnoSel.idTurno))}
                            onVolverAContactar={(t) => {
                                // El alta es un Modal de Bootstrap: lo abre la página y el detalle
                                // (overlay propio) se cierra, si no quedaría debajo.
                                setTurnoSel(null);
                                if (idsVolverAContactar.has(Number(t.idTurno))) {
                                    navigate('/listados/volver-a-contactar');
                                } else {
                                    setTurnoParaAgregar(t);
                                }
                            }}
                        />
                    </div>
                </div>
            )}

            <ModalReingresoCliente
                show={!!turnoReingreso}
                turnoOrigen={turnoReingreso}
                onCerrar={() => setTurnoReingreso(null)}
                onRegistrado={() => { setTurnoReingreso(null); cargarTurnos(); }}
            />

            <ModalAgregarVolverAContactar
                show={!!turnoParaAgregar}
                turno={turnoParaAgregar}
                onCerrar={() => setTurnoParaAgregar(null)}
                onAgregado={(registro) => {
                    const idAgregado = Number(registro?.idTurno ?? turnoParaAgregar?.idTurno);
                    setIdsVolverAContactar((previos) => new Set([...previos, idAgregado]));
                    setTurnoParaAgregar(null);
                    toast.success('Agregado al listado «Volver a contactar».');
                }}
            />
        </div>
    );
}
